import { useTranslation } from 'react-i18next'
import { ArrowUpRightIcon } from '@untitledui-icons/react/line'

// LinkedIn icon
const LinkedInIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
)

// Instagram icon
const InstagramIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
)

// Blog post data - from https://bonnie.co/stories/
const BLOG_POSTS = [
  {
    id: 1,
    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=534&fit=crop',
    category: 'Restaurant',
    title: 'How the Seafood Bar reduced guest service costs by 40% with Bonnie',
    excerpt: 'Boosted revenue and reduced service desk costs by 40%.',
    url: 'https://bonnie.co/interview-the-seafood-bars-success-with-bonnie/',
  },
  {
    id: 2,
    image: 'https://images.unsplash.com/photo-1559329007-40df8a9345d8?w=800&h=534&fit=crop',
    category: 'Restaurant',
    title: 'Boosting restaurant reservations with AI',
    excerpt: 'How AI phone handling can boost your revenue.',
    url: 'https://bonnie.co/stories/boosting-restaurant-reservations-with-ai/',
  },
  {
    id: 3,
    image: 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=800&h=534&fit=crop',
    category: 'Restaurant',
    title: 'Staff shortages & boosting availability at Republiek Bloemendaal',
    excerpt: 'How Bonnie helps solve internal staffing issues.',
    url: 'https://bonnie.co/stories/staff-shortages-and-boosting-availability-how-bonnie-helps-republiek-bloemendaal/',
  },
]

// Social button component
function SocialButton({ 
  href, 
  icon: Icon, 
  label 
}: { 
  href: string
  icon: React.FC
  label: string 
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="flex h-11 w-11 items-center justify-center rounded-lg border-2 border-[rgba(255,255,255,0.12)] bg-black shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] transition-colors hover:bg-[#1a1d22]"
    >
      <Icon />
    </a>
  )
}

// Blog post card component
function BlogPostCard({ 
  image, 
  category, 
  title, 
  excerpt,
  url,
}: { 
  image: string
  category: string
  title: string
  excerpt: string
  url: string
}) {
  return (
    <a 
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex min-w-0 flex-1 cursor-pointer flex-col gap-4 lg:min-w-[320px]"
    >
      {/* Image */}
      <div className="aspect-[3/2] w-full overflow-hidden rounded-2xl">
        <img
          src={image}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-2">
        {/* Category */}
        <span className="text-sm font-semibold leading-5 text-[#6941c6]">
          {category}
        </span>

        {/* Title and arrow */}
        <div className="flex gap-4">
          <h3 className="flex-1 text-lg font-semibold leading-7 text-[#181d27]">
            {title}
          </h3>
          <div className="pt-0.5">
            <ArrowUpRightIcon className="h-6 w-6 text-[#a4a7ae] transition-colors group-hover:text-[#181d27]" />
          </div>
        </div>

        {/* Excerpt */}
        <p className="text-base leading-6 text-[#535862]">
          {excerpt}
        </p>
      </div>
    </a>
  )
}

export function ConfirmationStep() {
  const { t } = useTranslation()

  return (
    <div className="flex w-full flex-col items-center">
      {/* Hero section */}
      <div className="flex w-full max-w-[440px] flex-col items-center gap-6 px-5 pt-12 text-center sm:px-0 sm:pt-[92px]">
        {/* Title and description */}
        <div className="flex w-full flex-col gap-2">
          <h1 className="text-[30px] font-semibold leading-[38px] tracking-[-0.72px] text-black sm:text-[36px] sm:leading-[44px]">
            {t('resources.signup.confirmation.title')}
          </h1>
          
          <div className="text-base leading-6 text-black">
            <p className="mb-4">
              {t('resources.signup.confirmation.message_1')}{' '}
              <span className="font-semibold">{t('resources.signup.confirmation.message_business_days')}</span>.
            </p>
            <p>{t('resources.signup.confirmation.message_2')}</p>
          </div>
        </div>

        {/* Social links */}
        <div className="flex items-center justify-center gap-6">
          <SocialButton
            href="https://www.linkedin.com/company/bonnie-co"
            icon={LinkedInIcon}
            label={t('resources.signup.confirmation.follow_linkedin')}
          />
          <SocialButton
            href="https://www.instagram.com/bonnie.hq"
            icon={InstagramIcon}
            label={t('resources.signup.confirmation.follow_instagram')}
          />
        </div>
      </div>

      {/* Blog posts section */}
      <div className="mt-16 flex w-full max-w-[1200px] flex-col gap-8 px-5 sm:mt-[120px] sm:gap-10 sm:px-6">
        {/* Divider */}
        <div className="h-px w-full bg-[#e9eaeb]" />

        {/* Header and posts */}
        <div className="flex flex-col gap-4">
          {/* Header row */}
          <div className="flex w-full items-center justify-between">
            <h2 className="text-lg font-semibold leading-7 text-black">
              {t('resources.signup.confirmation.blog_title')}
            </h2>
            <a
              href="https://bonnie.co/stories/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden items-center gap-1 text-sm font-semibold leading-5 text-[#6941c6] transition-colors hover:text-[#5930a8] sm:flex"
            >
              {t('resources.signup.confirmation.blog_discover_all')}
              <ArrowUpRightIcon className="h-5 w-5 text-[#9e77ed]" />
            </a>
          </div>

          {/* Blog posts grid - stacked on mobile, row on desktop */}
          <div className="flex w-full flex-col gap-10 lg:flex-row">
            {BLOG_POSTS.map((post) => (
              <BlogPostCard
                key={post.id}
                image={post.image}
                category={post.category}
                title={post.title}
                excerpt={post.excerpt}
                url={post.url}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
